import type { DocumentBlock } from '@platform/shared-types';
import { diffWords } from './wordDiff';
import type { WordDiffOp } from './wordDiff';

/**
 * Block-level diff for the version chain.
 *
 * Blocks are compared by their STABLE `id`, never by position. That is the whole
 * reason the system can report "Clause 4.2 changed" instead of re-diffing the
 * document as text: a block keeps its id when its version's blocks are copied
 * into the next draft, so reordering a document produces zero changes and
 * editing one paragraph produces exactly one.
 *
 * Type-only import from `@platform/shared-types`: this package is source-only,
 * consumed by Vite alias, and deliberately pulls in nothing at runtime here.
 */

export type BlockChange = 'unchanged' | 'changed' | 'added' | 'removed';

export interface BlockDiffEntry<T = DocumentBlock> {
  block: T;
  change: BlockChange;
  /**
   * Word-level detail for a `changed` block, ONLY when both the previous and
   * next block carry a plain-string `text` field (heading/paragraph/callout —
   * see documents.ts's `DocumentBlockSchema`; `list`/`table`/`mergeField`/
   * `signatureField`/`pageBreak` blocks have no single `text` to diff).
   * Absent for every other change kind. Deliberately diffs `text`, never
   * `runs` — a formatting-only edit (bold, italic) must not read as a
   * wording change, the same invariant `documents.ts`'s `runsMatchText`
   * schema refinement protects.
   */
  wordDiff?: WordDiffOp[];
}

/** Structural minimum `diffBlocks` needs. Every `DocumentBlock` satisfies it. */
export interface Identified {
  id: string;
}

/**
 * Deep structural equality over the plain JSON shapes blocks are made of.
 *
 * Hand-rolled rather than pulled from a library — this package must add zero
 * dependencies. Key ORDER is deliberately ignored (a `JSON.stringify`
 * comparison would report a spurious "changed" when a handler round-trips a
 * block through DynamoDB and returns its keys in a different order).
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  // undefined-valued keys are treated as absent, so `{ label: undefined }` and
  // `{}` compare equal — DynamoDB drops undefined on the way in.
  const ak = Object.keys(ao).filter((k) => ao[k] !== undefined);
  const bk = Object.keys(bo).filter((k) => bo[k] !== undefined);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (!Object.prototype.hasOwnProperty.call(bo, k)) return false;
    if (!deepEqual(ao[k], bo[k])) return false;
  }
  return true;
}

/** The plain-string `text` a block carries, or `undefined` if it has none (or it isn't a string). */
function textOf(block: unknown): string | undefined {
  if (block !== null && typeof block === 'object' && 'text' in block) {
    const t = (block as { text?: unknown }).text;
    if (typeof t === 'string') return t;
  }
  return undefined;
}

/**
 * Compare two block lists by id.
 *
 * Returns `next`'s blocks in `next`'s order, each tagged `unchanged`, `changed`
 * or `added`, followed by every block that existed in `prev` and is gone from
 * `next`, tagged `removed`. Removed blocks stay in the result on purpose: the
 * editor and the diff view both render them struck through rather than making
 * a deletion invisible.
 */
export function diffBlocks<T extends Identified>(
  prev: readonly T[],
  next: readonly T[],
): BlockDiffEntry<T>[] {
  const prevById = new Map<string, T>();
  for (const block of prev) prevById.set(block.id, block);

  const nextIds = new Set<string>();
  const out: BlockDiffEntry<T>[] = [];

  for (const block of next) {
    nextIds.add(block.id);
    const before = prevById.get(block.id);
    if (!before) {
      out.push({ block, change: 'added' });
    } else if (deepEqual(before, block)) {
      out.push({ block, change: 'unchanged' });
    } else {
      const beforeText = textOf(before);
      const afterText = textOf(block);
      const wordDiff = beforeText !== undefined && afterText !== undefined
        ? diffWords(beforeText, afterText)
        : undefined;
      out.push(wordDiff ? { block, change: 'changed', wordDiff } : { block, change: 'changed' });
    }
  }

  for (const block of prev) {
    if (!nextIds.has(block.id)) out.push({ block, change: 'removed' });
  }

  return out;
}

export interface BlockDiffSummary {
  unchanged: number;
  changed: number;
  added: number;
  removed: number;
  /** True when nothing at all differs between the two versions. */
  identical: boolean;
}

/** The counts shown above a diff and on the publish gate ("6 blocks changed"). */
export function summariseDiff(entries: readonly BlockDiffEntry<any>[]): BlockDiffSummary {
  const s: BlockDiffSummary = {
    unchanged: 0, changed: 0, added: 0, removed: 0, identical: true,
  };
  for (const e of entries) s[e.change] += 1;
  s.identical = s.changed === 0 && s.added === 0 && s.removed === 0;
  return s;
}
